import { useCallback, useState } from 'react'

import { applyLoginSuccessSideEffects } from './afterLoginSuccess'
import { LinkedInLoginPhase, LinkedInLoginStatus } from './constants'
import type {
	LinkedInLoginPhase as LinkedInLoginPhaseType,
	LinkedInLoginResult
} from './types'

const GENERIC_LOGIN_ERROR = 'Login failed'

type UseLinkedInPasswordLoginOptions = {
	skipWorkspaceAutomationModeCheck?: boolean
}

function useLinkedInPasswordLogin(options?: UseLinkedInPasswordLoginOptions) {
	const skipWorkspaceAutomationModeCheck =
		options?.skipWorkspaceAutomationModeCheck === true
	const [open, setOpen] = useState(false)
	const [phase, setPhase] = useState<LinkedInLoginPhaseType>(LinkedInLoginPhase.Ready)
	const [error, setError] = useState<string | null>(null)
	const [result, setResult] = useState<LinkedInLoginResult | null>(null)

	const close = useCallback(() => {
		setOpen(false)
		setPhase(LinkedInLoginPhase.Ready)
		setError(null)
		setResult(null)
	}, [])

	const start = useCallback(() => {
		setOpen(true)
		setPhase(LinkedInLoginPhase.Ready)
		setError(null)
		setResult(null)
	}, [])

	const finishCaptcha = useCallback(
		async (
			headers: LinkedInLoginResult['headers'],
			profile?: LinkedInLoginResult['profile'],
			correlationId?: string | null
		) => {
			const solved: LinkedInLoginResult = {
				status: LinkedInLoginStatus.Succeeded,
				headers,
				profile: profile ?? null
			}
			setResult(solved)
			const confirmed = skipWorkspaceAutomationModeCheck
				? true
				: await applyLoginSuccessSideEffects(solved, correlationId)
			if (!confirmed) {
				setError(GENERIC_LOGIN_ERROR)
				setPhase(LinkedInLoginPhase.Error)
				return
			}

			setPhase(LinkedInLoginPhase.Done)
		},
		[skipWorkspaceAutomationModeCheck]
	)

	const resultValue = {
		close,
		error,
		finishCaptcha,
		open,
		phase,
		result,
		setError,
		start
	}
	return resultValue
}

export { useLinkedInPasswordLogin }
